// Auto-Logout bei Inaktivität.
// Setzt einen Timer zurück, sobald der User etwas tut (Klick, Tastendruck,
// Mausbewegung, Scroll, Touch). Nach `timeoutMs` ohne Aktivität wird der
// Nutzer sauber ausgeloggt und auf /login umgeleitet.
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { authApi } from '../lib/api';
import { useAuthStore } from '../lib/store';

const DEFAULT_TIMEOUT_MS = 60 * 60 * 1000; // 1h
const EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

export function useInactivityLogout(timeoutMs = DEFAULT_TIMEOUT_MS) {
  const { isAuthenticated, clearUser } = useAuthStore();
  const timerRef = useRef(null);
  const isLoggingOutRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    const handleLogout = async () => {
      if (isLoggingOutRef.current) return;
      isLoggingOutRef.current = true;
      try {
        await authApi.logout().catch(() => {});
      } finally {
        clearUser();
        toast.info('Du wurdest wegen Inaktivität automatisch abgemeldet.', {
          duration: 6000,
        });
        // Sicher zur Login-Seite navigieren (Hard-Reload räumt Zustand auf)
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    };

    const resetTimer = () => {
      if (isLoggingOutRef.current) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(handleLogout, timeoutMs);
    };

    // Initial starten
    resetTimer();

    // Event Listener
    EVENTS.forEach((ev) => window.addEventListener(ev, resetTimer, { passive: true }));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      EVENTS.forEach((ev) => window.removeEventListener(ev, resetTimer));
    };
  }, [isAuthenticated, clearUser, timeoutMs]);
}
